import { memo } from 'react';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import PropTypes from 'prop-types';

import NameIcon from '#client/components/NameIcon';

import { AccountWrapperV2 } from './AccountCommon.styles';
import Label from './Label';

function AccountCommonV2({
  numberOfCharacters = 2,
  pillText,
  title,
  subtitle,
  buttonText,
  onClick,
  nameIcon,
  loading,
  disabled,
  statuses = [],
  buttonProps = {}
}) {
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  return (
    <AccountWrapperV2 aria-label="account">
      <Stack
        alignItems="center"
        direction="row"
        gap="16px"
        width="100%"
      >
        <Box display={{ xs: 'none', md: 'block' }}>
          <NameIcon
            circle
            name={nameIcon ?? title}
            numberOfCharacters={numberOfCharacters}
          />
        </Box>
        <Stack
          alignItems={{ xs: 'left', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          flexGrow={5}
          gap="16px"
          justifyContent="space-between"
        >
          <Stack
            alignItems="start"
            gap="4px"
          >
            {pillText && (
              <Text
                color="red"
                size="xs"
              >
                {pillText}
              </Text>
            )}
            <Text
              color="primary"
              sx={{ fontSize: isMobile ? '16px' : '18px' }}
              weight="medium"
            >
              {title}
            </Text>
            <Text
              color="secondary"
              size="xs"
            >
              {subtitle}
            </Text>
          </Stack>
          <Stack
            direction="row"
            gap={{ xs: '4px', md: '8px' }}
          >
            {statuses.map(({ status, bgColor, color }) => (
              <Label
                bgColor={bgColor}
                color={color}
                key={status}
                status={status}
              />
            ))}
          </Stack>
        </Stack>
        <Box alignSelf={{ xs: 'end', md: 'center' }}>
          <Button
            aria-label="select-account"
            disabled={disabled}
            loading={loading}
            onClick={onClick}
            role="button"
            size="small"
            variant="contained"
            {...buttonProps}
          >
            {buttonText}
          </Button>
        </Box>
      </Stack>
    </AccountWrapperV2>
  );
}

AccountCommonV2.propTypes = {
  numberOfCharacters: PropTypes.number,
  pillText: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  nameIcon: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  buttonText: PropTypes.string,
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      bgColor: PropTypes.string,
      color: PropTypes.string
    })
  ),
  buttonProps: PropTypes.object // eslint-disable-line react/forbid-prop-types
};

export default memo(AccountCommonV2);
